/* ==========================================================================
   LV- Visit Us — Laverne Diamonds
   assets/lv-visit.js

   Custom element so the Shopify editor lifecycle is native.

   The Google Maps embed is injected on click, never on page load. A live embed
   is roughly 1MB of script and blocks the main thread; on a section a visitor
   may never reach that is pure waste. The poster carries the first paint and
   the iframe replaces it only when asked for.
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('lv-visit')) return;

  const REDUCED = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LVVisit extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-visit]');
      if (!this.root) return;

      this.media = this.root.querySelector('[data-media]');
      this.btn = this.root.querySelector('[data-load-map]');

      this.slides = [...this.root.querySelectorAll('[data-slide]')];
      this.panes = [...this.root.querySelectorAll('[data-pane]')];
      this.dots = [...this.root.querySelectorAll('[data-branch-dot]')];
      this.index = 0;
      this._ac = new AbortController();

      this.reveal();
      this.bindMap();
      this.bindBranches();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this.btn && this._onLoad) this.btn.removeEventListener('click', this._onLoad);
      // One controller kills every branch listener at once, so re-adding the
      // section in the theme editor never leaves a stale handler behind.
      if (this._ac) this._ac.abort();
    }

    /* ---- branches ----------------------------------------------------------
       All photos and all text panes are already in the DOM. Switching only
       toggles classes, so there is no request and no layout jump on click.
       ---------------------------------------------------------------------- */
    bindBranches() {
      if (this.slides.length < 2) return;
      const sig = { signal: this._ac.signal };

      const prev = this.root.querySelector('[data-branch-prev]');
      const next = this.root.querySelector('[data-branch-next]');

      if (prev) prev.addEventListener('click', () => this.go(this.index - 1), sig);
      if (next) next.addEventListener('click', () => this.go(this.index + 1), sig);

      this.dots.forEach((d) => {
        d.addEventListener('click', () => this.go(Number(d.dataset.branchDot)), sig);
      });

      // Arrow keys work only once focus is inside the section, so they never
      // steal the page's own left/right behaviour.
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
      }, sig);
    }

    go(next) {
      const n = this.slides.length;
      if (n < 2) return;
      // wrap in both directions, so the arrows are never dead ends
      const i = ((next % n) + n) % n;
      if (i === this.index) return;
      this.index = i;

      this.slides.forEach((el, k) => {
        el.classList.toggle('is-active', k === i);
        // aria-hidden must track visibility or a screen reader announces
        // every branch's photo at once
        if (k === i) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', 'true');
      });

      this.panes.forEach((el, k) => {
        el.classList.toggle('is-active', k === i);
        el.hidden = k !== i;
      });

      this.dots.forEach((el, k) => {
        el.classList.toggle('is-active', k === i);
        el.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });

      // If a map was already loaded, swap it to the new branch rather than
      // leaving the visitor looking at the previous location.
      if (this.media && this.media.classList.contains('is-live')) {
        const frame = this.media.querySelector('iframe');
        const src = this.slides[i].getAttribute('data-map-src');
        if (frame && src) {
          frame.src = src;
          frame.setAttribute('title', this.slides[i].getAttribute('data-map-title') || 'Map');
        } else {
          // this branch has no map: drop back to the photo
          if (frame) frame.remove();
          this.media.classList.remove('is-live');
        }
      }

      this.syncLoadButton();
    }

    /* A branch may have no embed URL of its own; the button must not offer to
       load a map that does not exist. */
    syncLoadButton() {
      if (!this.btn || !this.slides.length) return;
      const src = this.slides[this.index].getAttribute('data-map-src');
      this.btn.hidden = !src;
    }

    /* ---- map ---------------------------------------------------------------
       The embed URL is printed into a data attribute rather than an iframe src,
       so the browser never requests it until the visitor opts in.
       ---------------------------------------------------------------------- */
    bindMap() {
      if (!this.btn || !this.media) return;

      // In branch mode the URL lives on the active slide, not the media box.
      const readSrc = () => (
        this.slides.length
          ? this.slides[this.index].getAttribute('data-map-src')
          : this.media.getAttribute('data-map-src')
      );
      const readTitle = () => (
        this.slides.length
          ? this.slides[this.index].getAttribute('data-map-title')
          : this.media.getAttribute('data-map-title')
      );

      if (!readSrc()) { this.btn.hidden = true; }
      if (!this.slides.length && !readSrc()) return;

      this._onLoad = () => {
        if (this.media.classList.contains('is-live')) return;
        const src = readSrc();
        if (!src) return;

        const frame = document.createElement('iframe');
        frame.src = src;
        frame.loading = 'lazy';
        frame.referrerPolicy = 'no-referrer-when-downgrade';
        frame.allowFullscreen = true;
        frame.setAttribute('title', readTitle() || 'Map');
        // A map is decorative next to a written address; keep it out of the
        // tab order so keyboard users are not trapped panning it.
        frame.setAttribute('tabindex', '-1');

        this.media.appendChild(frame);
        this.media.classList.add('is-live');
      };

      this.btn.addEventListener('click', this._onLoad);
    }

    /* ---- reveal ------------------------------------------------------------ */
    reveal() {
      const items = this.root.querySelectorAll('[data-animate]');
      if (!items.length) return;

      if (!('IntersectionObserver' in window) || REDUCED()) {
        this.root.classList.add('is-in');
        return;
      }
      this.root.classList.add('anim-ready');

      if (this.root.getBoundingClientRect().top < innerHeight * 0.9) {
        requestAnimationFrame(() => this.root.classList.add('is-in'));
        return;
      }
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting) return;
          this.root.classList.add('is-in');
          this._io.disconnect();
        });
      }, { threshold: 0.15 });
      this._io.observe(this.root);
    }
  }

  customElements.define('lv-visit', LVVisit);
})();
